import React from 'react'
import { listInvoices } from '../../../lib/billingApi'
import { formatMoney } from '../../../lib/utils'
import { loadAuth } from '../../../lib/authStorage'
import type { Invoice } from '../../../types/api'
import { Skeleton } from '../../../components/ui/Skeleton'

export function InvoiceList() {
  const isAdmin = loadAuth()?.role === 'ADMIN'
  const [invoices, setInvoices] = React.useState<Invoice[]>([])
  const [loading, setLoading] = React.useState(!isAdmin)
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (isAdmin) return
    let cancelled = false
    setLoading(true)
    listInvoices(0, 10)
      .then(data => {
        if (!cancelled) setInvoices(data.content)
      })
      .catch(e => {
        if (!cancelled) setError((e as { message?: string }).message ?? 'Failed to load invoices')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [isAdmin])

  if (isAdmin) return null
  if (loading) return <Skeleton height={60} borderRadius={8} />

  if (error) return (
    <div style={{ fontSize: 13, color: 'var(--red, #dc2626)', padding: '8px 0' }}>{error}</div>
  )

  if (invoices.length === 0) return (
    <div style={{ fontSize: 13, color: 'var(--text2)', padding: '12px 0' }}>No invoices yet.</div>
  )

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
      {invoices.map((inv, i) => (
        <div
          key={inv.id}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '10px 14px',
            borderBottom: i < invoices.length - 1 ? '1px solid var(--border)' : 'none',
            fontSize: 13,
            background: 'var(--surface2)',
          }}
        >
          <div>
            <div style={{ fontWeight: 500, color: 'var(--text)' }}>
              {new Intl.DateTimeFormat(undefined, { month: 'short', year: 'numeric' }).format(
                new Date(inv.issuedAt),
              )}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 2 }}>
              {inv.periodStart.slice(0, 10)} → {inv.periodEnd.slice(0, 10)}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span
              style={{
                fontSize: 11,
                padding: '2px 6px',
                borderRadius: 99,
                background: inv.status === 'PAID' ? 'rgba(16,185,129,0.1)' : 'var(--surface)',
                color: inv.status === 'PAID' ? '#065f46' : 'var(--text2)',
                fontFamily: 'DM Mono, monospace',
              }}
            >
              {inv.status}
            </span>
            <span style={{ fontWeight: 600, color: 'var(--text)' }}>
              {formatMoney(inv.totalCents, inv.currency)}
            </span>
            {inv.hostedInvoiceUrl && (
              <a
                href={inv.hostedInvoiceUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontSize: 12, color: 'var(--accent)', textDecoration: 'none' }}
              >
                View ↗
              </a>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}
